import React, { useContext, useMemo } from "react";
import { Championship, DiscardType, Race } from "api";
import { BoxCard, FormButton, LoggedUserContext } from "ui/componentes";
import MaterialTable, { MTableBodyRow } from "material-table";
import { LocalizationPTBr } from "util/MaterialTableUtil";
import { UploadRaceResults } from "ui/views/ChampionshipStandings/UploadRaceResults";
import { ChampionshipDriver } from "api/model/Championship/ChampionshipDriver";
import { useUploadResults } from "ui/views/ChampionshipStandings/useUploadResults";
import { useUpdateResults } from "ui/views/ChampionshipStandings/useUpdateResults";
import { Box, Tooltip } from "@material-ui/core";
import { useClearRaceResults } from "ui/views/ChampionshipStandings/useClearRaceResults";
import { ArrowDownward, ArrowUpward } from "@material-ui/icons";

interface RaceStandingsProps {
    race: Race;
    showTitle: boolean;
    championship: Championship;
    drivers: ChampionshipDriver[];
    selectedClass?: string;
}

const RaceStandings: React.FC<RaceStandingsProps> = ({
    race,
    showTitle,
    championship,
    drivers,
    selectedClass,
}) => {
    const loggedUser = useContext(LoggedUserContext);

    const raceResult = useMemo(
        () =>
            race.results
                ?.filter(
                    (result) =>
                        !championship.isMultiClass ||
                        result.className === selectedClass
                )
                .sort((result) => result.position) || [],
        [championship.isMultiClass, race.results, selectedClass]
    );
    const driverByIRacingId = useMemo(() => {
        const driverByIRacingId: { [key: string]: ChampionshipDriver } = {};
        drivers.forEach(
            (driver, index) =>
                (driverByIRacingId[driver.iracingId || index] = driver)
        );
        return driverByIRacingId;
    }, [drivers]);

    const { uploading, uploadResults } = useUploadResults();
    const { updating, updateResults } = useUpdateResults();
    const {
        updating: updatingRaceResults,
        clearRaceResults,
    } = useClearRaceResults();

    return (
        <MaterialTable
            components={{
                Container: BoxCard,
                Row: (props) => {
                    const { data } = props;
                    let title = "";
                    if (data.discardType === DiscardType.CHANGE_CAR) {
                        title = "DESCARTE APLICADO DEVIDO À TROCA DE CARRO";
                    }
                    return <MTableBodyRow {...props} title={title} />;
                },
            }}
            title={`Bateria ${race.raceNumber}`}
            data={raceResult}
            columns={[
                {
                    field: "classPosition",
                    title: "Pos.",
                    type: "numeric",
                    width: 50,
                    cellStyle: () => ({
                        fontWeight: "bold",
                        padding: "6px 8px 6px 8px",
                    }),
                },
                {
                    field: "carNumber",
                    title: "Nº",
                    render: (standings) => {
                        const driver = driverByIRacingId[standings.iRacingId];
                        return `# ${driver?.usedNumber || standings.carNumber}`;
                    },
                    type: "numeric",
                    width: 50,
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "driverName",
                    title: "Piloto",
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                    render: (standings) => {
                        const driver = driverByIRacingId[standings.iRacingId];
                        return driver?.displayName || standings.driverName;
                    },
                },
                {
                    field: "teamName",
                    title: "Equipe",
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                    hidden: !championship.championshipByTeam,
                },
                {
                    field: "carName",
                    title: "Carro",
                    hidden: !championship.isMultiClass,
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "className",
                    title: "Classe",
                    hidden: !championship.isMultiClass,
                    align: "center",
                    width: 100,
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "classStartPosition",
                    title: "Pos.Inicial",
                    type: "numeric",
                    width: 50,
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "gainedPositions",
                    title: "Pos.Ganhas / Perdidas",
                    type: "numeric",
                    width: 50,
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                    render: (result) => {
                        if (!result.gainedPositions) {
                            return "-";
                        }
                        let color = "";
                        if (!result.discard) {
                            color =
                                result.gainedPositions > 0 ? "green" : "red";
                        }
                        const Icon =
                            result.gainedPositions > 0
                                ? ArrowUpward
                                : ArrowDownward;
                        return (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-end"
                                fontWeight="bold"
                                color={color}
                            >
                                <Icon style={{ fontSize: 17 }} />
                                {result.gainedPositions}
                            </Box>
                        );
                    },
                },
                {
                    field: "completedLapsPercentange",
                    title: "Voltas",
                    type: "numeric",
                    width: 50,
                    render: (result) =>
                        result.completedLapsPercentange ? (
                            <Tooltip
                                title={`${result.completedLapsPercentange}%`}
                            >
                                <span>{result.lapsComplete}</span>
                            </Tooltip>
                        ) : (
                            ""
                        ),
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "fastestLap",
                    title: "Volta rápida",
                    type: "numeric",
                    width: 150,
                    sorting: false,
                    render: (result) =>
                        result.fastestLapTime && result.fastLap ? (
                            <Tooltip title={`Volta ${result.fastLap}`}>
                                <span>{result.fastestLapTime}</span>
                            </Tooltip>
                        ) : (
                            ""
                        ),
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "averageLapTime",
                    title: "Tempo medio",
                    type: "numeric",
                    width: 80,
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "incidents",
                    title: "Inc.",
                    type: "numeric",
                    width: 50,
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "originalPoints",
                    title: "Pts. sem bonus e penalidades",
                    type: "numeric",
                    width: 50,
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                },
                {
                    field: "penaltyChampPoints",
                    title: "Pts. descontados",
                    render: (result) => {
                        if (!result.penaltyChampPoints) return "-";

                        const title = result.messagesForPenaltyChampPoints
                            ?.length
                            ? result.messagesForPenaltyChampPoints.join("</br>")
                            : "";
                        return (
                            <Tooltip title={title}>
                                <span>{`-${result.penaltyChampPoints}`}</span>
                            </Tooltip>
                        );
                    },
                    type: "numeric",
                    width: 50,
                },
                {
                    field: "bonusPoints",
                    title: "Pts. bonus",
                    cellStyle: () => ({ padding: "6px 8px 6px 8px" }),
                    render: (result) => {
                        if (!result.bonusPoints) return "-";

                        const title = result.messagesForBonusPoints?.length
                            ? result.messagesForBonusPoints.join(" | ")
                            : "";
                        return (
                            <Tooltip title={title}>
                                <span>{`+${result.bonusPoints}`}</span>
                            </Tooltip>
                        );
                    },
                    type: "numeric",
                    width: 50,
                },
                {
                    field: "points",
                    title: "Pontos",
                    type: "numeric",
                    width: 50,
                    cellStyle: (result) => ({
                        fontWeight: "bold",
                        padding: "6px 8px 6px 8px",
                        color: typeof result === "string" ? "red" : "",
                    }),
                },
            ]}
            localization={LocalizationPTBr}
            options={{
                paging: false,
                search: false,
                showTitle,
                padding: "dense",
                headerStyle: { padding: "6px 8px 6px 8px" },
                rowStyle: (data) => {
                    if (data.discard) {
                        return {
                            color: "grey",
                            textDecoration: "line-through",
                        };
                    }
                    return {};
                },
            }}
            actions={[
                {
                    isFreeAction: true,
                    icon: () => (
                        <FormButton
                            loading={updatingRaceResults}
                            onClick={() => {
                                clearRaceResults(championship.id, race.id);
                            }}
                            color="secondary"
                            disabled={!raceResult.length}
                        >
                            Remover resultados
                        </FormButton>
                    ),
                    onClick: () => "",
                    disabled: updatingRaceResults || !raceResult.length,
                    hidden: !loggedUser.isAdmin,
                    tooltip:
                        "AO CLICAR OS RESULTADOS DESSA ETAPA SERÃO EXCLUÍDOS",
                },
                {
                    isFreeAction: true,
                    icon: () => (
                        <FormButton
                            loading={updating}
                            onClick={() => {
                                updateResults(championship.id, race.id);
                            }}
                            color="primary"
                            disabled={!raceResult.length}
                        >
                            Reprocessar Pontuação
                        </FormButton>
                    ),
                    onClick: () => "",
                    disabled: updating || !raceResult.length,
                    hidden: !loggedUser.isAdmin,
                    tooltip:
                        "Ao clicar será reprocessado pontos bonus, desconto por penalidades e a pontuação final da etapa",
                },
                {
                    isFreeAction: true,
                    icon: () => (
                        <UploadRaceResults
                            race={race}
                            uploading={uploading}
                            uploadResults={uploadResults}
                            championship={championship}
                        />
                    ),
                    onClick: () => "",
                    disabled: uploading,
                    hidden: !loggedUser.isAdmin,
                },
            ]}
        />
    );
};

export { RaceStandings };
